<template>
  <main class="findid">
    <section class="findid__card" aria-labelledby="findid-title">
      <div class="findid__head">
        <h1 id="findid-title" class="findid__title">아이디 찾기</h1>
        <p class="findid__desc">
          가입 시 입력한 이름과 전화번호를 입력하면 가입된 아이디를 확인할 수 있습니다.
        </p>
      </div>

      <p v-if="noticeMsg" class="findid__notice">
        {{ noticeMsg }}
      </p>
      <p v-if="errorMsg" class="findid__error" role="alert">
        {{ errorMsg }}
      </p>

      <div class="findid__body">
        <label class="findid__label" for="userName">이름</label>
        <div class="findid__field">
          <input
            id="userName"
            v-model.trim="userName"
            type="text"
            autocomplete="name"
            placeholder="이름을 입력하세요"
            @keyup.enter="handleSearchUserId"
          />
        </div>

        <label class="findid__label" for="phone">전화번호</label>
        <div class="findid__field">
          <input
            id="phone"
            v-model.trim="phone"
            type="text"
            inputmode="numeric"
            autocomplete="tel"
            placeholder="전화번호를 입력하세요"
            @input="handlePhoneInput"
            @keyup.enter="handleSearchUserId"
          />
        </div>
        <p class="findid__hint">
          가입 시 입력한 전화번호를 입력해주세요.
        </p>

        <div class="findid__actions findid__actions--single">
          <button
            class="findid__btn"
            type="button"
            :disabled="loading"
            @click="handleSearchUserId"
          >
            {{ loading ? '조회 중...' : '아이디 찾기' }}
          </button>
        </div>
      </div>

      <div v-if="foundEmail" class="findid__result">
        <p class="findid__result-label">가입된 아이디</p>
        <div class="findid__result-box">
          {{ foundEmail }}
        </div>
      </div>

      <div class="findid__footer">
        <button class="findid__footer-link" type="button" @click="goLogin">
          로그인으로 돌아가기
        </button>
        <button class="findid__footer-link" type="button" @click="goResetPassword">
          비밀번호 찾기
        </button>
      </div>
    </section>
  </main>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { searchUserIdAjax } from '@/api/user'

const router = useRouter()

const userName = ref('')
const phone = ref('')
const foundEmail = ref('')

const loading = ref(false)
const errorMsg = ref('')
const noticeMsg = ref('')

const clearMessages = () => {
  errorMsg.value = ''
  noticeMsg.value = ''
}

const validate = () => {
  if (!userName.value) {
    errorMsg.value = '이름을 입력해주세요.'
    return false
  }

  if (!phone.value) {
    errorMsg.value = '전화번호를 입력해주세요.'
    return false
  }

  if (phone.value.length < 10) {
    errorMsg.value = '올바른 전화번호를 입력해주세요.'
    return false
  }

  return true
}

const handlePhoneInput = (event) => {
  const onlyNumber = event.target.value.replace(/[^0-9]/g, '')
  phone.value = onlyNumber
}

const handleSearchUserId = async () => {
  clearMessages()
  foundEmail.value = ''

  if (!validate()) return

  loading.value = true

  try {
    const result = await searchUserIdAjax({
      userName: userName.value,
      phone: phone.value
    })

    if ((result?.result || '').toLowerCase() === 'ok') {
      errorMsg.value = ''
      foundEmail.value = result?.data || ''
      noticeMsg.value = result?.resultMsg || '아이디를 찾았습니다.'
    } else {
      foundEmail.value = ''
      noticeMsg.value = ''
      errorMsg.value = result?.resultMsg || '가입된 정보가 없습니다.'
    }
  } catch (error) {
    errorMsg.value = '아이디 조회 중 오류가 발생했습니다.'
  } finally {
    loading.value = false
  }
}

const goLogin = () => {
  router.push('/login')
}

const goResetPassword = () => {
  router.push('/reset-password')
}
</script>

<style scoped>
.findid {
  font-family: system-ui, -apple-system, "Segoe UI", "Noto Sans KR",
    "Apple SD Gothic Neo", "Malgun Gothic", Arial, sans-serif;
  min-height: 80vh;
  display: grid;
  place-items: center;
  background: #f8fafc;
}

.findid__card {
  width: 100%;
  max-width: 580px;
  background: #fff;
  border: 1px solid #e5e7eb;
  margin: 30px 0;
  border-radius: 16px;
  padding: 44px 36px;
  box-shadow: 0 10px 30px rgba(17, 24, 39, 0.06);
}

.findid__head {
  margin-bottom: 28px;
}

.findid__title {
  margin: 0 0 10px;
  font-size: 1.6rem;
  font-weight: 800;
  letter-spacing: -0.2px;
  color: #0f172a;
}

.findid__desc {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.6;
  color: #475569;
}

.findid__notice,
.findid__error {
  margin: 0 0 16px;
  font-size: 0.95rem;
  border-radius: 10px;
  padding: 10px 12px;
}

.findid__notice {
  color: #334155;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
}

.findid__error {
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.findid__body {
  margin-top: 8px;
}

.findid__label {
  display: block;
  margin: 14px 0 8px;
  font-weight: 700;
  font-size: 0.95rem;
  color: #0f172a;
}

.findid__field {
  position: relative;
}

.findid__field input {
  width: 100%;
  height: 58px;
  padding: 0 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 17px;
  color: #0f172a;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.findid__field input::placeholder {
  color: #94a3b8;
}

.findid__field input:focus {
  outline: none;
  border-color: #a5b4fc;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.15);
}

.findid__hint {
  margin: 8px 0 0;
  font-size: 0.88rem;
  line-height: 1.5;
  color: #64748b;
}

.findid__actions {
  display: grid;
  gap: 12px;
  margin-top: 28px;
}

.findid__actions--single {
  grid-template-columns: 1fr;
}

.findid__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 58px;
  font-size: 17px;
  font-weight: 700;
  border-radius: 14px;
  border: 0;
  cursor: pointer;
  color: #fff;
  background: #1d2088;
  transition: background 0.15s ease;
}

.findid__btn:hover {
  background: #220e52;
}

.findid__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.findid__result {
  margin-top: 28px;
  padding: 20px;
  border: 1px solid #dbe4ff;
  border-radius: 14px;
  background: linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%);
}

.findid__result-label {
  margin: 0 0 12px;
  font-size: 0.9rem;
  font-weight: 700;
  color: #475569;
}

.findid__result-box {
  min-height: 58px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #c7d2fe;
  color: #1d2088;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.2px;
  word-break: break-all;
}

.findid__footer {
  margin-top: 22px;
  display: flex;
  justify-content: center;
  gap: 18px;
  flex-wrap: wrap;
}

.findid__footer-link {
  padding: 0;
  border: 0;
  background: transparent;
  color: #1d2088;
  text-decoration: underline;
  font-weight: 600;
  cursor: pointer;
}

@media (max-width: 640px) {
  .findid__card {
    padding: 28px 18px;
  }
}

@media (max-width: 480px) {
  .findid {
    min-height: auto;
    padding: 24px 16px;
  }

  .findid__footer {
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }
}
</style>